import { useState, useCallback } from 'react'
import axios, { type AxiosError } from 'axios'
import { type CustomError, useApiUtils } from './useApiUtils'
import { type Configuration } from '../core/providers/ConfigurationContext'
import {
  type TelemetryType,
  type CreateTrackSessionRequest,
  type LapDto,
  type TrackSessionDto,
  messageOf,
} from '@pitbrain/shared'
import { type RawTelemetrySource } from '../features/upload/rawTelemetrySource'
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js'
import { SeverityLevel } from '@microsoft/applicationinsights-web'

export interface TrackSessionApiReturnType {
  saveTrackSession: (
    request: CreateTrackSessionRequest
  ) => Promise<string | null>
  getSharedLapsForTrackSession: (
    tracksession: string
  ) => Promise<LapDto[] | null>
  getAllTrackSessions: () => Promise<TrackSessionDto[]>
  getTrackSessionByName: (
    trackSessionName: string,
    telemetryTypes: readonly TelemetryType[]
  ) => Promise<TrackSessionDto | null>
  getTrackSession: (
    trackSessionId: string,
    telemetryTypes: readonly TelemetryType[]
  ) => Promise<TrackSessionDto | null>
  storeSessionTelemetry: (
    trackSessionId: string,
    source: RawTelemetrySource
  ) => Promise<boolean>
  error: CustomError | null
}

export const useTrackSessionApi = (
  bffConfig: Configuration | null
): TrackSessionApiReturnType => {
  const [error, setError] = useState<CustomError | null>(null)
  const { getAuthToken, handleError, performApiCall } = useApiUtils(bffConfig)
  const appInsights = useAppInsightsContext()
  const resetErrors = () => {
    setError(null)
  }
  const saveTrackSession = useCallback(
    async (request: CreateTrackSessionRequest): Promise<string | null> => {
      if (!bffConfig) return null
      try {
        const token = await getAuthToken()
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/tracksession`,
          request,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )
        return await Promise.resolve(response.data)
      } catch (error) {
        const axiosError = error as AxiosError
        if (axiosError.response?.status === 409) {
          setError({
            error: true,
            message:
              'A session with this name already exists on the server. Use LOAD to open it instead of saving again.',
            code: 409,
          })
          return null
        }
        const handledError = handleError(axiosError)
        setError(handledError)
        return null
      }
    },
    [bffConfig, getAuthToken]
  )

  const getSharedLapsForTrackSession = useCallback(
    async (tracksession: string): Promise<LapDto[] | null> => {
      if (!bffConfig) return null
      try {
        const token = await getAuthToken()
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/tracksession/${tracksession}/laps/shared`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        return response.data as LapDto[]
      } catch (error) {
        const handledError = handleError(error as AxiosError)
        setError(handledError)
        return null
      }
    },
    [bffConfig, getAuthToken]
  )

  const getAllTrackSessions = useCallback(async (): Promise<
    TrackSessionDto[]
  > => {
    if (!bffConfig) return []
    try {
      const token = await getAuthToken()
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/tracksession`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      )

      return response.data as TrackSessionDto[]
    } catch (error) {
      const handledError = handleError(error as AxiosError)
      setError(handledError)
      return []
    }
  }, [bffConfig, getAuthToken])

  const getTrackSessionByName = useCallback(
    async (
      trackSessionName: string,
      telemetryTypes: readonly TelemetryType[] = []
    ): Promise<TrackSessionDto | null> => {
      if (!bffConfig) return null
      try {
        const token = await getAuthToken()
        const telemetryQuery = telemetryTypes
          .map((t) => `telemetryTypes=${t}`)
          .join('&')
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/tracksession?name=${trackSessionName}${telemetryTypes.length ? '&' : ''}${telemetryQuery}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        return response.data as TrackSessionDto
      } catch (error) {
        const axiosError = error as AxiosError
        const handledError = handleError(axiosError)
        setError(handledError)
        if (axiosError.response?.status === 404) {
          return null
        }
        throw new Error(
          'Could not verify whether this session already exists on the server. Please try again.'
        )
      }
    },
    [bffConfig, getAuthToken]
  )

  const getTrackSession = useCallback(
    async (
      trackSessionId: string,
      telemetryTypes: readonly TelemetryType[] = []
    ): Promise<TrackSessionDto | null> => {
      if (!bffConfig) return null
      try {
        const token = await getAuthToken()
        const telemetryQuery = telemetryTypes
          .map((t) => `telemetryTypes=${t}`)
          .join('&')
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/tracksession/${trackSessionId}?${telemetryQuery}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        return response.data as TrackSessionDto
      } catch (error) {
        const handledError = handleError(error as AxiosError)
        setError(handledError)
        return null
      }
    },
    [bffConfig, getAuthToken]
  )

  const storeSessionTelemetry = useCallback(
    async (
      trackSessionId: string,
      source: RawTelemetrySource
    ): Promise<boolean> => {
      if (!bffConfig) return false
      try {
        const token = await getAuthToken()
        const form = new FormData()
        form.append(
          'file',
          new File([source.content], source.fileName, {
            type: source.contentType,
          })
        )

        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/file/session/${trackSessionId}/telemetry`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        )
        return true
      } catch (error) {
        const axiosError = error as AxiosError | undefined
        if (appInsights) {
          appInsights.trackException(
            {
              exception: new Error('Could not archive raw telemetry'),
              severityLevel: SeverityLevel.Error,
            },
            {
              trackSessionId,
              fileName: source.fileName,
              bytes: source.content.size,
              status: axiosError?.response?.status ?? 0,
              reason: messageOf(error),
            }
          )
        }
        console.warn(
          `Could not archive raw telemetry for session ${trackSessionId}; the session itself was saved.`,
          error
        )
        return false
      }
    },
    [bffConfig, getAuthToken, appInsights]
  )

  return {
    storeSessionTelemetry,
    saveTrackSession: performApiCall(saveTrackSession, resetErrors),
    getSharedLapsForTrackSession: performApiCall(
      getSharedLapsForTrackSession,
      resetErrors
    ),
    getAllTrackSessions: performApiCall(getAllTrackSessions, resetErrors),
    getTrackSessionByName: performApiCall(getTrackSessionByName, resetErrors),
    getTrackSession: performApiCall(getTrackSession, resetErrors),
    error,
  }
}
